import { Navigate, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './lib/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './components/auth/LoginPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import Dashboard from './components/dashboard/Dashboard';
import { MainLayout } from './components/common/layout/MainLayout';
import { DisabledFeaturePage } from './components/common/layout/DecommissionNotice';
import { EditProfilePage } from './components/profile/EditProfilePage';
import { ProfilePage } from './components/profile/ProfilePage';
import { CommunitiesPage } from './components/communities/CommunitiesPage';
import { CommunityPage } from './components/communities/CommunityPage';
import { SettingsPage } from './components/settings/SettingsPage';
import { WorkspacePage } from './components/workspace/WorkspacePage';
import { NotFound } from './components/common/layout/NotFound';
import { captureAppException } from './utils/errorCaptureUtils';
import { ManageCommunityPage } from './components/communities/ManageCommunityPage';
import { ManageWorkspacePage } from './components/workspace/ManageWorkspacePage';
import { useUnhandledRejectionHandler } from './hooks/useUnhandledRejectionHandler';

// Error test component
const ErrorTestPage = () => {
  const handleTestError = () => {
    throw new Error("This is a test error for Sentry");
  };

  const handleHandledError = () => {
    try {
      // Simulate an API call that fails
      throw new Error("This is a handled error with custom context");
    } catch (error) {
      // Log to console
      console.error("Handled error occurred:", error);

      // Capture with new utility
      captureAppException(error, {
        tags: {
          context: 'error_test_page',
          error_type: 'handled_error',
          test_scenario: 'api_simulation'
        },
        extra: {
          custom_message: "This error was caught and handled explicitly",
          timestamp: new Date().toISOString()
        }
      });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="p-8 bg-white rounded-lg shadow-md space-y-6">
        <h1 className="text-2xl font-bold mb-4">Sentry Error Test</h1>

        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold mb-2">Unhandled Error</h2>
            <button
              onClick={handleTestError}
              className="w-full px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
            >
              Trigger Unhandled Error
            </button>
            <p className="mt-2 text-sm text-gray-600">
              This will throw an error directly, demonstrating unhandled error capture.
            </p>
          </div>

          <div className="border-t pt-4">
            <h2 className="text-lg font-semibold mb-2">Handled Error</h2>
            <button
              onClick={handleHandledError}
              className="w-full px-4 py-2 bg-yellow-500 text-white rounded-sm hover:bg-yellow-600 transition-colors"
            >
              Trigger Handled Error
            </button>
            <p className="mt-2 text-sm text-gray-600">
              This will catch the error, log it to console, and explicitly send to Sentry with custom context.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  // Initialize global unhandled rejection handler
  useUnhandledRejectionHandler();

  return (
    <AuthProvider>
      <Routes>
        {/* Public routes - authentication and event related */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/invite" element={<DisabledFeaturePage featureName="Workspace invites" />} />
        <Route path="/evaluator/accept" element={<DisabledFeaturePage featureName="Evaluator invites" />} />
        <Route path="/e/:id" element={<DisabledFeaturePage featureName="Events" />} />
        <Route path="/ticket/:secret" element={<DisabledFeaturePage featureName="Tickets" />} />

        <Route path="/register/onboarding" element={<DisabledFeaturePage featureName="Onboarding" />} />
        <Route path="/" element={
          <ProtectedRoute>
            <MainLayout allowIncompleteProfile>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        } />
        
        {/* Events and related flows are disabled during decommission. */}
        <Route
          path="/events/*"
          element={
            <ProtectedRoute>
              <MainLayout allowIncompleteProfile>
                <DisabledFeaturePage featureName="Events" />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Communities */}
        <Route
          path="/communities"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CommunitiesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/communities/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CommunityPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/communities/:id/manage"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ManageCommunityPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/communities/pair-discord"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Discord pairing" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/communities/pair-github-org"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="GitHub pairing" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Other protected routes */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Projects" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Projects" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout allowIncompleteProfile>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <MainLayout allowIncompleteProfile>
                <EditProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <WorkspacePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:workspaceId/subscription"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Subscription management" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:id/manage"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ManageWorkspacePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout allowIncompleteProfile>
                <SettingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Subscription management" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/network"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Networking" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/community-analytics"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Community analytics" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Marketplace" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Marketplace" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace/:id/documentation/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="Marketplace" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/competitions/my-applications"
          element={<DisabledFeaturePage featureName="Competitions" />}
        />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <NotFound />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Internal test routes */}
        <Route path="/error-test" element={<ErrorTestPage />} />
        <Route
          path="/ui-test"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DisabledFeaturePage featureName="UI testing" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

export default App;